import React, { memo, useCallback, useState } from 'react';
import { Button, Col, Input, InputRef, Popover, Row, Space } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { NotificationTemplateFragment, NotificationTemplateVariable } from '@/graphql/generated/graphql';
import Picker, { IEmojiData } from 'emoji-picker-react';
import TemplateSelector from '../TemplateSelector';
import VariableSelect from '../Variables';
import { If } from '@/components';

type Props = {
  content: string;
  variables?: NotificationTemplateVariable[];
  onChangePlainTemplateText: (content: string) => void;
  showTemplates?: boolean;
  showVariables?: boolean;
  notificationTemplates?: NotificationTemplateFragment[];
  onSelectTemplateData?: (templateId: string) => void;
  maxLength?: number;
};

const PlainTextEditor: React.FC<Props> = memo(
  ({
    content,
    variables,
    onChangePlainTemplateText,
    notificationTemplates,
    onSelectTemplateData,
    showTemplates = false,
    showVariables = false,
    maxLength = 250,
  }) => {
    const inputRef = React.useRef<InputRef>(null);
    const [emojiPicker, shoEmojiPicker] = useState(false);

    const onAddVariable = useCallback(
      (value: string) => {
        onChangePlainTemplateText(content.concat(value, ' '));
        inputRef.current?.focus();
      },
      [content, onChangePlainTemplateText]
    );

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChangePlainTemplateText(e.target.value);
    };

    const onEmojiClick = (event: React.MouseEvent<Element, MouseEvent>, emojiObject: IEmojiData) => {
      onChangePlainTemplateText(content.concat(emojiObject.emoji, ' '));
      inputRef.current?.focus();
    };

    const showEmojiPicker = () => {
      shoEmojiPicker((prevState) => !prevState);
    };

    const onSelectTemplate = useCallback(
      (id: string) => {
        onSelectTemplateData?.(id);
      },
      [onSelectTemplateData]
    );

    return (
      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Space>
            <>
              <If condition={showTemplates}>
                <TemplateSelector onSelectTemplate={onSelectTemplate} notificationTemplates={notificationTemplates} />
              </If>
              <If condition={showVariables}>
                <VariableSelect variables={variables} onAddVariable={onAddVariable} />
              </If>
            </>
            <Popover
              content={<Picker disableSkinTonePicker native onEmojiClick={onEmojiClick} disableAutoFocus={true} />}
              onOpenChange={showEmojiPicker}
              open={emojiPicker}
              trigger="click"
            >
              <Button icon={<SmileOutlined />} type="primary">
                Insert Emoji
              </Button>
            </Popover>
          </Space>
        </Col>
        <Col span={24}>
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 8 }}
            defaultValue={content}
            value={content}
            onChange={onChange}
            showCount
            maxLength={maxLength}
            ref={inputRef}
            allowClear
            placeholder="Text message"
          />
        </Col>
      </Row>
    );
  }
);

export default PlainTextEditor;
